import { registerOverlay } from 'klinecharts'
import { NO_DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'
import { type OverlayFigure } from './overlayUtils'
import { CHART_FONT_FAMILY } from './chartColors'

/**
 * Daily candles are a regular-session artifact: before the 09:30 ET open,
 * the data feed already serves a "today" bar built from nothing but premarket
 * trades. The chart suppresses that forming bar (DataLayer) and shows the live
 * premarket price as a y-axis pill instead (this overlay).
 */

const ET = 'America/New_York'
const REGULAR_OPEN_MINUTES = 9 * 60 + 30

const dateDtf = new Intl.DateTimeFormat('en-US', {
  timeZone: ET,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})
const timeDtf = new Intl.DateTimeFormat('en-US', {
  timeZone: ET,
  hour: 'numeric',
  minute: 'numeric',
  hour12: false,
})

function minutesOfDayET(timestamp: number): number {
  const parts = timeDtf.formatToParts(new Date(timestamp))
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10)
  const minute = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10)
  return hour * 60 + minute
}

/**
 * True when `now` is before the 09:30 ET regular-session open on the same ET
 * calendar date as the bar — i.e. the daily bar exists only because of
 * premarket trades and should not render as a candle yet.
 */
export function isPremarketFormingDailyBar(barTimestamp: number, now: number): boolean {
  if (dateDtf.format(new Date(barTimestamp)) !== dateDtf.format(new Date(now))) return false
  return minutesOfDayET(now) < REGULAR_OPEN_MINUTES
}

export const EXTENDED_SESSION_PRICE_MARK_NAME = 'extendedSessionPriceMark'

export type ExtendedSessionLabel = 'Pre' | 'Post'

/**
 * Solid pill colors for the Pre/Post mark. Deliberately NOT the settings'
 * `preMarketColor` / `afterHoursColor`: those are near-transparent background
 * shading tints (9% / 5% grey) and would make an invisible pill.
 */
export const EXTENDED_SESSION_COLORS: Record<ExtendedSessionLabel, string> = {
  Pre: '#f0932b',
  Post: '#5c6bc0',
}

/** Pill text: white on both session colors (they are saturated, not light). */
export const EXTENDED_PILL_TEXT = '#ffffff'

/**
 * Rendered height of a price pill (11px text + 2px top/bottom padding; the
 * native last-price pill uses the same metrics). Stacking two pills exactly
 * this far apart makes them touch without overlapping.
 */
export const EXTENDED_PILL_HEIGHT = 15

/**
 * Where the Pre/Post pill goes: on its own price, unless that would overlap the
 * last-price pill, in which case it stacks directly above it (extended price
 * higher) or below it (lower). The dotted line keeps the true price either way.
 */
export function resolveExtendedPillY(y: number, lastY: number | null, above: boolean): number {
  if (lastY == null || !Number.isFinite(lastY)) return y
  if (Math.abs(y - lastY) >= EXTENDED_PILL_HEIGHT) return y
  return above ? lastY - EXTENDED_PILL_HEIGHT : lastY + EXTENDED_PILL_HEIGHT
}

interface ExtendedSessionFigureInput {
  /** Pixel y of the extended-hours price (the dotted line). */
  y: number
  /** Pixel y of the tag, when it had to dodge the last-price pill. */
  pillY?: number
  /** Main-pane width; the line spans it and the tag sits at its right edge. */
  width: number
  color: string
  label: ExtendedSessionLabel | string
}

/**
 * Main-pane figures: a full-width dotted line at the extended-hours price and
 * a session tag ("Pre" / "Post") flush against the price axis, so the tag and
 * the axis price pill read as one label.
 */
export function buildExtendedSessionFigures(input: ExtendedSessionFigureInput): OverlayFigure[] {
  const { y, width, color, label } = input
  if (!Number.isFinite(y)) return []
  const tagY = input.pillY ?? y
  return [
    {
      type: 'line',
      ignoreEvent: true,
      attrs: {
        coordinates: [
          { x: 0, y },
          { x: width, y },
        ],
      },
      styles: { style: 'dashed', dashedValue: [1, 3], size: 1, color },
    },
    {
      type: 'text',
      ignoreEvent: true,
      attrs: { x: width, y: tagY, text: label, align: 'right', baseline: 'middle' },
      styles: {
        style: 'fill',
        color: EXTENDED_PILL_TEXT,
        size: 11,
        family: CHART_FONT_FAMILY,
        weight: 'normal',
        backgroundColor: color,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 2,
        paddingBottom: 2,
        // Square right corners: the tag butts against the price pill as one label.
        borderRadius: [2, 0, 0, 2],
      },
    },
  ]
}

/** Axis figure: the price pill, in the session color, aligned like the native last-price label. */
export function buildExtendedSessionAxisFigures(input: {
  y: number
  value: number
  pricePrecision: number
  color: string
}): OverlayFigure[] {
  const { y, value, pricePrecision, color } = input
  if (!Number.isFinite(y)) return []
  return [
    {
      type: 'text',
      ignoreEvent: true,
      attrs: { x: 0, y, text: value.toFixed(pricePrecision), align: 'left', baseline: 'middle' },
      styles: {
        style: 'fill',
        color: EXTENDED_PILL_TEXT,
        size: 11,
        family: CHART_FONT_FAMILY,
        weight: 'normal',
        backgroundColor: color,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 2,
        paddingBottom: 2,
        // Square left corners, to join the session tag.
        borderRadius: [0, 2, 2, 0],
      },
    },
  ]
}

interface MarkTemplateArgs {
  coordinates: Array<{ x: number; y: number }>
  overlay: { points: Array<{ value?: number }>; extendData?: unknown }
  bounding: { width: number }
  precision: { price: number }
  yAxis?: { convertToPixel: (value: number) => number } | null
}

function markExt(overlay: MarkTemplateArgs['overlay']): {
  label: string
  color: string
  lastPrice: number | null
} {
  const ext = (overlay.extendData ?? {}) as { label?: string; color?: string; lastPrice?: number }
  return {
    label: ext.label ?? 'Pre',
    color: ext.color ?? EXTENDED_SESSION_COLORS.Pre,
    lastPrice: typeof ext.lastPrice === 'number' ? ext.lastPrice : null,
  }
}

/** The pill's y for this render: dodges the last-price pill (see resolveExtendedPillY). */
function pillYFor(args: MarkTemplateArgs, y: number, value: number): number {
  const { lastPrice } = markExt(args.overlay)
  const lastY = lastPrice != null && args.yAxis ? args.yAxis.convertToPixel(lastPrice) : null
  return resolveExtendedPillY(y, lastY, lastPrice == null || value >= lastPrice)
}

/**
 * Pre/Post price mark: dotted line + session tag in the chart body, price pill
 * on the axis. Created with a single `{ value }` point; label + color ride in
 * `extendData`. Read-only chrome, id-tracked by ChartManager.
 */
export const registerExtendedSessionPriceMarkOverlay = registerOnce(() => {
  registerOverlay({
    name: EXTENDED_SESSION_PRICE_MARK_NAME,
    totalStep: 2,
    lock: true,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: (args: MarkTemplateArgs) => {
      const c = args.coordinates[0]
      const value = args.overlay.points[0]?.value
      if (!c || value == null) return []
      const { label, color } = markExt(args.overlay)
      return buildExtendedSessionFigures({
        y: c.y,
        pillY: pillYFor(args, c.y, value),
        width: args.bounding.width,
        color,
        label,
      })
    },
    createYAxisFigures: (args: MarkTemplateArgs) => {
      const c = args.coordinates[0]
      const value = args.overlay.points[0]?.value
      if (!c || value == null) return []
      const { color } = markExt(args.overlay)
      const { precision } = args
      return buildExtendedSessionAxisFigures({
        y: pillYFor(args, c.y, value),
        value,
        pricePrecision: precision.price,
        color,
      })
    },
  } as OverlayTemplateParam)
})
